import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Card, Form, Switch, Radio, Input, Typography, Space, Divider, Alert, Button, Empty, Tooltip, Spin } from "antd"
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useWatch } from "antd/es/form/Form"
import { notification } from "ui/Notification"
import { computeDiscountAmount } from "utils/discountUtils"
import {
  attachUserPaymentMethod,
  failedPaymentMethods,
  requestedPaymentMethods,
  getPaymentMethods,
  paymentMethodsSelector,
  selectedPaymentMethodIdSelector,
  setSelectedPaymentMethodId,
  paymentMethodsLoadingSelector,
  isPaypalConnectedSelector,
  isStripeConnectedSelector
} from "store/user"
import { updateBookingSelections } from "store/experiences"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import { hasNoUserBookingChanges } from "../bookingUtils"
import { CardNumberElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { receiveErrors, resetErrors } from "modules/errors/reducer"
import PaymentMethodList from "./PaymentMethodList"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import styles from "./BookingForm.module.css"
import AddCardModal from "./AddCardModal"
import { setClientId } from "store/bookings"
import { colors } from "themes/variables"
import { numberToCurrency } from "helpers/string"
import dayjs from "dayjs"
import { PAYMENT_OPTIONS } from "../constants"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"
import { validatePaymentAmount } from "../bookingUtils"
import stripeIcon from "assets/imagesV2/settings/stripe.svg"
import { isFeatureEnabled } from "config/features"
import { paypalLogoUrl } from "helpers/constant"
const { Text } = Typography

const PaymentMethodForm = ({
  form,
  isEdit,
  booking,
  isCashPaymentRequired = true,
  isPriceChanged,
  existingClientId,
  setExistingClientId,
  clientEmail,
  appliedPromos = []
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [modalForm] = Form.useForm()
  const { guide } = useSelector((state) => state.user)
  const paymentMethods = useSelector(paymentMethodsSelector)
  const paymentMethodsLoading = useSelector(paymentMethodsLoadingSelector)
  const isPaypalConnected = useSelector(isPaypalConnectedSelector)
  const hasPaymentMethods = !!paymentMethods.length
  const isStripeConnected = useSelector(isStripeConnectedSelector)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isAddingCard, setIsAddingCard] = useState(false)
  const depositOnly = useWatch("depositOnly", form)
  const depositPercent = useWatch("depositPercent", form)
  const customDepositAmount = useWatch("customDepositAmount", form)
  const paymentType = useWatch("paymentType", form)
  const collectPayment = useWatch("collectPayment", form)
  const clientId = useSelector((state) => state.bookings.clientId)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const {
    experienceById: { experienceDetail },
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)
  const paymentOption = useWatch("paymentOption", form)
  const isDepositPercentage = !experienceDetail?.full_payment_required && experienceDetail?.deposit_percentage
  const isPastDate = bookingSelections?.date && dayjs(bookingSelections.date).isBefore(dayjs(), "day")

  // Calculate all payment values using the utility function
  const { discount_amount } = booking?.payment_summary ?? {}
  const promoDiscount = appliedPromos.reduce(
    (sum, p) => sum + computeDiscountAmount(p, bookingSelections?.baseAmount ?? 0, bookingSelections?.addOnsTotal ?? 0),
    0
  )
  // When a new promo is applied, it REPLACES the original booking discount.
  const effectiveEditDiscount = isEdit && discount_amount > 0 && appliedPromos.length === 0 ? discount_amount : 0
  const totalDiscount = effectiveEditDiscount + promoDiscount

  const { amount, percent, depositAmount, depositDueNow, isDeposit, balanceAmount, refundDueNow } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent: depositPercent || 0,
    customDepositAmount: customDepositAmount || 0,
    paymentOption,
    discountAmount: totalDiscount
  })

  const isCollectPayment = collectPayment === "yes"
  const paymentMessage =
    depositDueNow > 0
      ? t("booking.payment_message_deposit", { amount: numberToCurrency(balanceAmount) })
      : t("booking.payment_message_remaining", { amount: numberToCurrency(balanceAmount) })

  const stripe = useStripe()
  const elements = useElements()

  const isPaymentLinkType = (type) => type === "link" || type === "paypalLink"

  const handlePaymentTypeChange = (e) => {
    const value = e.target.value
    handlePaymentDetailsChange("paymentType", value)
    if (value === "card") {
      dispatch(setSelectedPaymentMethodId(paymentMethods[0]?.payment_method_id))
    } else {
      dispatch(setSelectedPaymentMethodId(""))
    }

    if (isPaymentLinkType(value)) {
      form.setFieldsValue({
        paymentOption: depositOnly ? PAYMENT_OPTIONS.deposit_cash : PAYMENT_OPTIONS.full_link
      })
    }

    // Reset any specific payment UI elements when changing payment types
    setIsAddingCard(false)
  }

  const handlePaymentDetailsChange = (key, value) => {
    if (key === "depositOnly") {
      if (value === true) {
        form.setFieldsValue({
          depositOnly: true,
          depositPercent: isDepositPercentage ? experienceDetail?.deposit_percentage : 10,
          customDepositAmount: "",
          paymentOption: PAYMENT_OPTIONS.deposit_cash
        })
      } else {
        form.setFieldsValue({
          depositOnly: false,
          depositPercent: "",
          paymentOption: isPaymentLinkType(paymentType) ? PAYMENT_OPTIONS.full_link : ""
        })
      }
    } else if (key === "depositPercent" && value !== "other") {
      form.setFieldsValue({ depositPercent: Number(value), customDepositAmount: "" })
    } else if (key === "customDepositAmount") {
      form.setFieldsValue({ customDepositAmount: value === "" ? "" : Number(value) })
    } else {
      form.setFieldsValue({ [key]: value })
    }
  }

  const onCancelModal = () => {
    modalForm.resetFields()
    setIsModalVisible(false)
  }

  const onAttachHandler = ({ paymentMethodId, userId }) => {
    dispatch(setSelectedPaymentMethodId(paymentMethodId))
    dispatch(setClientId(userId))
    if (elements._elements) {
      for (const element of elements._elements) {
        element.clear?.()
      }
    }
    notification.success({
      message: t("booking.stripe_added_success")
    })
    onCancelModal()
  }

  const createPaymentMethod = async () => {
    setIsAddingCard(true)

    try {
      const formData = await modalForm.validateFields()

      if (!stripe || !elements) {
        setIsAddingCard(false)
        return false
      }

      const cardNumberElement = elements.getElement(CardNumberElement)

      if (!cardNumberElement) {
        notification.error({
          message: t("CardNumberElement not mounted yet")
        })
        setIsAddingCard(false)
        return false
      }

      dispatch(requestedPaymentMethods())
      dispatch(resetErrors())

      const param = {
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name: formData.cardHolderName,
          address: {
            postal_code: formData.zip
          }
        }
      }
      const { paymentMethod, error } = await stripe.createPaymentMethod(param)
      dispatch(failedPaymentMethods())
      if (error) {
        if (error.type === "card_error") {
          dispatch(
            receiveErrors({
              data: { message: { card_error: error.message } },
              showToast: false
            })
          )
        } else {
          notification.error({
            message: t("booking.error_messages.stripe_error")
          })
        }
        setIsAddingCard(false)
        return false
      }
      const values = form.getFieldsValue(true)
      const isNewUser = !clientId || clientId === "null" || clientId === "undefined"

      const payload = {
        new_user: isNewUser,
        id: clientId,
        payment_method_id: paymentMethod.id,
        guide_id: isNewUser && guide?.id,
        client_attributes: isNewUser
          ? {
              first_name: values?.firstName,
              last_name: values?.lastName,
              email: values?.email,
              phone_number: values?.phone_number?.fullNumber,
              country_code: values?.phone_number?.countryCode,
              full_name: values?.existing_client?.label
            }
          : {}
      }

      const res = await dispatch(attachUserPaymentMethod(payload))
      const paymentMethodId = res?.payment_methods?.[0]?.payment_method_id
      const userId = res?.user_id
      if (setExistingClientId && userId) {
        setExistingClientId(userId)
      }
      dispatch(setClientId(userId))
      if (paymentMethodId && userId) {
        onAttachHandler({ paymentMethodId, userId })
      }
    } catch (error) {
      dispatch(failedPaymentMethods())
      console.error(error)
    } finally {
      setIsModalVisible(false)
      setIsAddingCard(false)
    }
  }

  useEffect(() => {
    if (paymentType === "cash") {
      form.setFieldsValue({
        depositPercent: "",
        paymentOption: "",
        customDepositAmount: ""
      })
    }
  }, [paymentType, form])

  // Auto-select default paymentType on mount if none is set yet
  useEffect(() => {
    if (paymentType === "card" && paymentMethods.length > 0 && !selectedPaymentMethodId) {
      dispatch(setSelectedPaymentMethodId(paymentMethods[0]?.payment_method_id))
      return
    }
    if (paymentType) return
    if (isStripeConnected) {
      form.setFieldsValue({ paymentType: "card" })
      dispatch(setSelectedPaymentMethodId(paymentMethods[0]?.payment_method_id))
    } else if (isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && clientEmail) {
      form.setFieldsValue({ paymentType: "paypalLink" })
    } else if (isCashPaymentRequired && !isDeposit) {
      form.setFieldsValue({ paymentType: "cash" })
    }
  }, [paymentType, isStripeConnected, isPaypalConnected, clientEmail, isCashPaymentRequired, isDeposit, form, dispatch, paymentMethods]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate breakdown when paymentType or selected card changes on step 2 (ExperienceDetail is unmounted)
  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const currentAmount = bookingSelections?.amount
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!hasGuestCounts || !tripLength || paymentType === undefined) return
    // In edit mode, skip until experienceDetail is loaded — otherwise an incomplete calc
    // silently drops the card fee and overwrites the booking's correct total.
    if (isEdit && !experienceDetail?.id) return
    // In edit mode, if the user hasn't changed anything yet, leave the booking's saved total
    // alone. Recomputing here can introduce drift (e.g. missing card fee from a partial calc).
    if (isEdit && hasNoUserBookingChanges(bookingSelections, booking)) return

    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const platformFeeSetting = experienceDetail?.platform_fee_setting || booking?.platform_fee_setting
    const selectedCard = paymentMethods.find((m) => m.payment_method_id === selectedPaymentMethodId)
    const editCardFeeEnabled = !!(
      experienceDetail?.assigned_custom_fees?.some(
        (f) => f.id === null && f.title === "Card Processing Fee" && f.is_active !== false
      ) ||
      Number(booking?.payment_summary?.card_processing_fee) > 0
    )
    const cardFeeInfo = selectedCard?.card_fee_info ?? (editCardFeeEnabled ? { rate: 0.029, fixed: 0.3 } : null)
    const { total, breakdown } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
      paymentType,
      isEdit,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting,
      cardFeeInfo
    }) || { total: 0, breakdown: [] }

    if (total !== currentAmount) {
      dispatch(updateBookingSelections({ amount: total, breakdown }))
    }
  }, [paymentType, selectedPaymentMethodId, paymentMethods, experienceDetail?.id]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clientId) {
      dispatch(getPaymentMethods(clientId))
    }
  }, [clientId, isAddingCard, dispatch])

  const depositAlert = `${t("booking.deposit_alert_base", {
    depositAmount: numberToCurrency(depositAmount),
    percent: percent,
    totalAmount: numberToCurrency(amount)
  })} ${
    paymentOption === "deposit_and_balance_later"
      ? t("booking.deposit_alert_balance_later", { balanceAmount: numberToCurrency(balanceAmount) })
      : t("booking.deposit_alert_balance_cash", { balanceAmount: numberToCurrency(balanceAmount) })
  }`

  useEffect(() => {
    if (hasPaymentMethods) {
      form.setFields([
        {
          name: "selectedPaymentMethodId",
          errors: []
        }
      ])
    }
  }, [hasPaymentMethods, form, booking])

  const paymentMethod = booking?.payment_history
  const latestMethodType = paymentMethod?.[0]?.method
  const last4 = paymentMethod?.[0]?.last4
  let selectedPaymentType = "cash"
  if (latestMethodType === "paypal" && last4 === null) {
    selectedPaymentType = "paypalLink"
  } else if (last4) {
    selectedPaymentType = "card"
  }
  // Only set paymentType in form if isEdit is true
  useEffect(() => {
    if (isEdit && form.getFieldValue("paymentType") !== selectedPaymentType) {
      form.setFieldsValue({ paymentType: selectedPaymentType })
    }
  }, [booking, selectedPaymentType, form, isEdit])
  return (
    <Spin spinning={paymentMethodsLoading} tip="loading...">
      {!isEdit && isCashPaymentRequired && !isPastDate && (
        <Form.Item name="depositOnly" valuePropName="checked">
          <Space>
            <Switch checked={depositOnly} onChange={(checked) => handlePaymentDetailsChange("depositOnly", checked)} />
            <Text>{t("booking.labels.collect_deposit")}</Text>
            <Tooltip title={t("booking.collect_deposit_info")}>
              <InfoCircleOutlined className={styles.errorIcon} />
            </Tooltip>
          </Space>
        </Form.Item>
      )}
      <div style={{ display: depositOnly ? "block" : "none" }}>
        <Form.Item name="depositPercent" label={t("booking.labels.deposit_amount")}>
          <Radio.Group onChange={(e) => handlePaymentDetailsChange("depositPercent", e.target.value)}>
            <Space direction="horizontal">
              {isDepositPercentage && <Radio value={experienceDetail?.deposit_percentage}>{experienceDetail?.deposit_percentage}%</Radio>}
              <Radio value="other">Other</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        {depositPercent === "other" && (
          <Form.Item
            name={"customDepositAmount"}
            label={t("booking.labels.custom_deposit_amount")}
            className={styles.customDeposit}
            rules={[{ required: depositPercent === "other", message: t("booking.error_messages.custom_deposit_amount") }]}
          >
            <Input
              placeholder="Enter amount"
              style={{ width: 200 }}
              maxLength={3}
              value={customDepositAmount}
              onChange={(e) => {
                let value = e.target.value.replaceAll(/\D/g, "")
                if (Number(value) > 100) value = "100"
                handlePaymentDetailsChange("customDepositAmount", value)
              }}
              addonAfter="%"
            />
          </Form.Item>
        )}

        <Form.Item name="paymentOption" label={t("booking.labels.how_will_client_pay")}>
          <Radio.Group onChange={(e) => handlePaymentDetailsChange("paymentOption", e.target.value)}>
            <Space direction="vertical">
              <Radio value={PAYMENT_OPTIONS.deposit_later}>{t("booking.labels.pay_deposit_card")}</Radio>
              <Radio value={PAYMENT_OPTIONS.deposit_cash}>{t("booking.labels.pay_deposit_cash")}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Alert message={depositAlert} type="info" className={styles.paymentAlert} />
        <Divider />
      </div>
      {isEdit && (
        <>
          {!isDeposit && (
            <Form.Item
              name="collectPayment"
              label={
                <span>
                  {t("booking.labels.collect_payment_now")}
                  <Tooltip title={t("booking.labels.collect_payment_now_tooltip")}>
                    <InfoCircleOutlined className={styles.infoNoticeIcon} style={{ marginLeft: 8 }} />
                  </Tooltip>
                </span>
              }
            >
              <Radio.Group value={collectPayment} onChange={(e) => handlePaymentDetailsChange("collectPayment", e.target.value)}>
                <Space direction="horizontal" wrap>
                  <Radio value="yes">{t("global.yes")}</Radio>
                  <Radio value="no">{t("global.no")}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          )}

          {isPriceChanged && isCollectPayment && paymentType !== "cash" && (
            <div className={styles.infoNotice}>
              <InfoCircleOutlined className={styles.infoNoticeIcon} />
              <span className={styles.infoNoticeText}>{paymentMessage}</span>
            </div>
          )}
        </>
      )}
      <div style={{ display: !isEdit || collectPayment !== "no" ? "block" : "none" }}>
        {isEdit && !isDeposit && <Divider />}
        <Form.Item
          name="paymentType"
          label={t("booking.labels.choose_payment_method")}
          rules={[{ required: true, message: t("booking.error_messages.payment_method_required") }]}
        >
          <Radio.Group value={paymentType || selectedPaymentType} onChange={handlePaymentTypeChange}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {/* Stripe Payment Options */}
              {isStripeConnected && (
                <div>
                  <div className={styles.paymentProviderGroup}>
                    <div className={styles.paymentProviderHeader}>
                      <img src={stripeIcon} alt="Stripe" height={24} />
                      <Text strong>{t("booking.stripe_payments")}</Text>
                    </div>
                    <Space direction="horizontal" wrap className={styles.paymentOptions}>
                      <Radio value="card">{t("booking.existing_new_card")}</Radio>
                      <Radio value="link">
                        {t("booking.send_payment_link")}
                      </Radio>
                    </Space>
                  </div>

                  {paymentType === "card" && (
                    <Form.Item
                      name="selectedPaymentMethodId"
                      rules={[{ required: !hasPaymentMethods, message: t("booking.error_messages.no_payment_method") }]}
                    >
                      <div style={{ height: "fit-content", maxHeight: "215px", overflowY: "auto", scrollbarWidth: "none" }}>
                        {!paymentMethodsLoading && hasPaymentMethods ? (
                          <PaymentMethodList
                            existingClientId={existingClientId}
                            active={selectedPaymentMethodId}
                            paymentMethods={paymentMethods}
                            clientId={clientId}
                            className="vstack gap-15"
                          />
                        ) : (
                          !isAddingCard && (
                            <Card className={styles.noCardAdded}>
                              <Empty
                                image={<NoCardImage />}
                                description={
                                  <>
                                    <p>{t("booking.labels.no_card_title")}</p>
                                    <Button icon={<PlusOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
                                      {t("booking.labels.add_card")}
                                    </Button>
                                  </>
                                }
                              />
                            </Card>
                          )
                        )}
                      </div>
                      {!paymentMethodsLoading && hasPaymentMethods && (
                        <div>
                          <Button
                            type="link"
                            icon={<PlusOutlined />}
                            style={{
                              color: colors.sportsmanBrand,
                              fontWeight: 500,
                              fontSize: 16,
                              marginTop: 10
                            }}
                            onClick={() => setIsModalVisible(true)}
                          >
                            {t("booking.labels.add_card")}
                          </Button>
                        </div>
                      )}
                    </Form.Item>
                  )}
                </div>
              )}

              {/* PayPal Payment Options - only shown if connected and feature enabled */}
              {isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && (
                <div className={styles.paymentProviderGroup}>
                  <div className={styles.paymentProviderHeader}>
                    <img src={paypalLogoUrl} alt="PayPal" height={24} />
                    <Text strong>{t("booking.paypal_payments")}</Text>
                  </div>
                  <Space direction="horizontal" wrap className={styles.paymentOptions}>
                    <Radio value="paypalLink">
                      {t("booking.send_payment_link")}
                    </Radio>
                  </Space>
                </div>
              )}

              {/* Cash Option */}
              {isCashPaymentRequired && !isDeposit && (
                <div className={styles.paymentProviderGroup}>
                  <div className={styles.paymentProviderHeader}>
                    <Text strong>{t("booking.other_payment_methods")}</Text>
                  </div>
                  <Space direction="horizontal" wrap className={styles.paymentOptions}>
                    <Radio value="cash">{t("booking.cash")}</Radio>
                  </Space>
                </div>
              )}
            </Space>
          </Radio.Group>
        </Form.Item>
        <div style={{ marginBottom: 10 }}>
          {isPaymentLinkType(paymentType) && <Text>{t("booking.payment_link_info")}</Text>}
          {paymentType === "cash" && collectPayment === "yes" && (
            <>
              {!isEdit && <Text>{t("booking.payment_cash_info")}</Text>}
              {isEdit && (
                <Form.Item
                  name="recordPayment"
                  label={t("booking.labels.record_payment")}
                  rules={[
                    {
                      validator: (_, value) =>
                        validatePaymentAmount(value, {
                          maxAmount: refundDueNow || balanceAmount,
                          t
                        })
                    }
                  ]}
                >
                  <Input placeholder={t("booking.labels.record_payment")} prefix="$" type="number" min="0.01" step="0.01" />
                </Form.Item>
              )}
            </>
          )}
        </div>

        {isModalVisible && (
          <AddCardModal
            visible={isModalVisible}
            onCancel={() => onCancelModal()}
            onSubmit={() => createPaymentMethod()}
            form={modalForm}
            loading={isAddingCard}
          />
        )}
      </div>
    </Spin>
  )
}

PaymentMethodForm.propTypes = {
  form: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  isCashPaymentRequired: PropTypes.bool,
  isPriceChanged: PropTypes.bool,
  booking: PropTypes.object.isRequired,
  existingClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setExistingClientId: PropTypes.func,
  clientEmail: PropTypes.string,
  appliedPromos: PropTypes.array
}

export default PaymentMethodForm
